/**
 * Opening a recording at the moment it is played or downloaded (VAPT 2026-10).
 *
 * A private recording's playback URL is a CloudFront signed URL that lapses —
 * at its length plus an hour after the list was loaded. A page left open longer
 * than that, or a viewing started too close to the end, would get a 403 or stop
 * mid-video. So the URL the list came with is used only while it still lasts
 * through a full viewing started now; otherwise the recording is asked for
 * again, which mints a fresh one. A public recording's URL never lapses.
 */

import { absolutizeRecordingUrl, getRecording, type Recording } from './recordings.api'

/** What a page holds for one recording. */
export interface HeldPlayback {
  id: string
  url: string | null
  /** Epoch ms the URL stops opening; null for one that does not. */
  expiresAt: number | null
  durationSeconds: number | null
}

/** Spare time on top of the recording's length before a held URL is replaced. */
export const PLAYBACK_REFRESH_MARGIN_MS = 5 * 60_000

type FetchOne = (id: string) => Promise<Pick<Recording, 'playbackUrl'>>

/** Epoch ms from the API's ISO string; null when it does not expire. */
export function expiryOf(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/** The held playback state of a recording from the API. */
export function heldPlayback(r: Recording): HeldPlayback {
  return {
    id: r.id,
    url: absolutizeRecordingUrl(r.playbackUrl),
    expiresAt: expiryOf(r.playbackUrlExpiresAt),
    durationSeconds: r.durationSeconds,
  }
}

/** Whether the held URL still lasts through a whole viewing started at `now`. */
export function lastsThroughViewing(held: HeldPlayback, now: number): boolean {
  if (!held.url) return false
  if (held.expiresAt === null) return true
  return held.expiresAt - now >= (held.durationSeconds ?? 0) * 1000 + PLAYBACK_REFRESH_MARGIN_MS
}

/** A URL that plays the recording through: the held one, or a fresh one. */
export async function freshPlaybackUrl(
  held: HeldPlayback,
  now: number = Date.now(),
  fetchOne: FetchOne = getRecording,
): Promise<string | null> {
  if (lastsThroughViewing(held, now)) return held.url
  const r = await fetchOne(held.id)
  return absolutizeRecordingUrl(r.playbackUrl)
}

/**
 * Play a recording in a new tab.
 *
 * Opened inside the click, before any request: a tab opened after an await is
 * no longer the user's gesture, and the browser blocks it as a popup. When the
 * URL must be fetched first, the tab opens blank and is pointed at it once it
 * arrives, or closed if it does not.
 */
export function openPlayback(
  held: HeldPlayback,
  deps: {
    open?: (url: string, features?: string) => Window | null
    fetchOne?: FetchOne
    now?: number
  } = {},
): void {
  const open = deps.open ?? ((url: string, features?: string) => window.open(url, '_blank', features))
  const now = deps.now ?? Date.now()
  if (lastsThroughViewing(held, now) && held.url) {
    open(held.url, 'noopener')
    return
  }
  // Without `noopener`, or there would be no tab to point later; the tab's
  // link back to this page is cut by hand instead.
  const tab = open('about:blank')
  if (!tab) return
  tab.opener = null
  void freshPlaybackUrl(held, now, deps.fetchOne).then(
    (url) => {
      if (url) tab.location.href = url
      else tab.close()
    },
    () => tab.close(),
  )
}

/**
 * Download a recording: the same URL rules, then a link clicked for it. An
 * anchor needs no user gesture, so the request may come first.
 */
export async function downloadPlayback(
  held: HeldPlayback,
  filename: string,
  deps: { fetchOne?: FetchOne; now?: number; click?: (url: string, filename: string) => void } = {},
): Promise<void> {
  const url = await freshPlaybackUrl(held, deps.now ?? Date.now(), deps.fetchOne)
  if (!url) throw new Error('No playback URL')
  const click =
    deps.click ??
    ((href: string, name: string) => {
      const a = document.createElement('a')
      a.href = href
      a.download = name
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    })
  click(url, filename)
}
